import { categoryLabels, shortCategory, textWidth } from './labels';
import { ValueScale, valueScale } from './scale';
import {
  ComboAxisSide,
  ComboCategoryAxis,
  ComboSeries,
  ComboValueAxis,
} from './types';

export interface ComboLayout {
  plot: { left: number; top: number; width: number; height: number };
  /** The width each category takes on the plot. */
  band: number;
  left: ValueScale | null;
  right: ValueScale | null;
  /** How far the category labels turn, as resolved. */
  rotate: 0 | 45 | 90;
  /** Every how-many categories a label is drawn. */
  step: number;
  /** The category labels as drawn (shortened, cut to fit). */
  labels: string[];
}

export interface ComboLayoutInput {
  width: number;
  height: number;
  categories: string[];
  series: ComboSeries[];
  stacked: boolean;
  xAxis: ComboCategoryAxis;
  yAxis: ComboValueAxis;
  y2Axis?: ComboValueAxis;
  dataLabels: boolean;
  fontSize: number;
}

/** Whether a series is part of the stack of a stacked chart. */
export function isStacked(series: ComboSeries, stacked: boolean): boolean {
  return stacked && series.type === 'bar' && series.axis === 'left';
}

/** Every value an axis has to show, stacks summed per category. */
function axisValues(
  input: ComboLayoutInput,
  side: ComboAxisSide,
): number[] | null {
  const own = input.series.filter(entry => entry.axis === side);
  if (!own.length) return null;
  const values: number[] = [];
  const stack = own.filter(entry => isStacked(entry, input.stacked));
  input.categories.forEach((_, i) => {
    let up = 0;
    let down = 0;
    for (const entry of stack) {
      const value = entry.values[i] ?? 0;
      if (value >= 0) up += value;
      else down += value;
    }
    values.push(up, down);
  });
  for (const entry of own)
    if (!isStacked(entry, input.stacked))
      for (const value of entry.values)
        if (value !== undefined && Number.isFinite(value)) values.push(value);
  return values;
}

function tickBand(
  scale: ValueScale | null,
  axis: ComboValueAxis | undefined,
  fontSize: number,
): number {
  if (!scale || !axis?.show) return scale ? 4 : 0;
  const widest = Math.max(
    ...scale.ticks.map(tick => textWidth(axis.format(tick), fontSize)),
  );
  return widest + 6 + (axis.title ? fontSize + 6 : 0);
}

/** Where everything goes on a combo chart of the given size. */
export function comboLayout(input: ComboLayoutInput): ComboLayout {
  const { width, height, fontSize: f, xAxis } = input;
  const leftValues = axisValues(input, 'left');
  const rightValues = axisValues(input, 'right');
  const probe = (values: number[] | null, axis?: ComboValueAxis) =>
    values ? valueScale(values, 1, { min: axis?.min, max: axis?.max }) : null;
  const leftBand = tickBand(probe(leftValues, input.yAxis), input.yAxis, f);
  const rightBand = rightValues
    ? tickBand(probe(rightValues, input.y2Axis), input.y2Axis, f)
    : 8;
  const plotWidth = Math.max(width - leftBand - rightBand, 40);
  const count = Math.max(input.categories.length, 1);
  const band = plotWidth / count;
  const { rotate, step, labels } = categoryLabels({
    labels: input.categories.map(shortCategory),
    centers: input.categories.map((_, i) => leftBand + band * (i + 0.5)),
    band,
    width,
    depth: height * 0.35,
    rotate: xAxis.rotate,
    time: xAxis.time,
    fontSize: f,
  });
  const widest = Math.max(0, ...labels.map(label => textWidth(label, f)));
  const labelBand = !xAxis.show
    ? 4
    : rotate === 0
    ? f + 8
    : rotate === 45
    ? widest * 0.71 + f + 6
    : widest + 8;
  const bottom = labelBand + (xAxis.show && xAxis.title ? f + 6 : 0);
  const top = input.dataLabels ? f + 6 : f / 2 + 2;
  const plotHeight = Math.max(height - top - bottom, 40);
  return {
    plot: { left: leftBand, top, width: plotWidth, height: plotHeight },
    band,
    left: leftValues
      ? valueScale(leftValues, plotHeight, {
          min: input.yAxis.min,
          max: input.yAxis.max,
        })
      : null,
    right: rightValues
      ? valueScale(rightValues, plotHeight, {
          min: input.y2Axis?.min,
          max: input.y2Axis?.max,
        })
      : null,
    rotate,
    step,
    labels,
  };
}
